/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback } from 'react';
import Loader from 'react-loader-spinner';
import { useDispatch } from 'react-redux';
import { FaArchive } from 'react-icons/fa';
import { openDialog } from '../../../../../../../app/dialog/dialog.actions';
import { ExporterButton } from '../../../../ExporterButton';
import { ExportQueryParams } from '../../../../ExporterButton/types';
import { deleteBoard } from '../../../../../../../app/page/page-storage/meeting-boards/meeting-boards.actions';
import { boardSelectors } from '../../../../../../../app/resource/boards/board/board.selectors';
import { IBoard } from '../../../../../../../app/resource/boards/board/board.types';
import { meetingSimpleSelectors } from '../../../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { IMeetingSimple } from '../../../../../../../app/resource/meeting-simple/meeting-simple.types';
import { useApiRequestState } from '../../../../../../../utils/api.utils';
import { useSelect, useSelectWithParams } from '../../../../../../../utils/selector.utils';
import { IBoardFormProps } from '../../../../forms/BoardForm/types';
import { HeaderUsers } from './HeaderUsers';
import { authSelectors } from '../../../../../../../app/auth/auth.selectors';
import { userSelectors } from '../../../../../../../app/resource/user/user.selectors';
import { isMemberPartOfMeeting } from '../../../../../../../utils/user.utils';
import { IBoardHeaderProps, IInfoTooltipProps } from './types';
import { closeDialog } from '../../../../../../../app/dialog/dialog.actions';
import { meetingBoardsPageSelectors } from '../../../../../../../app/page/page-storage/meeting-boards/meeting-boards.selectors';
import { IMeetingBoards } from '../../../../../../../app/page/page-storage/meeting-boards/meeting-boards.types';
import { taskSimpleSelectors } from '../../../../../../../app/resource/task-simple/task-simple.selectors';
import { ITaskSimple } from '../../../../../../../app/resource/task-simple/task-simple.types';
import { InfoIconPopup } from '../../../../InfoIconPopup';
import { agendaItemsPageSelectors } from '../../../../../../../app/page/page-storage/agenda-items/agenda-items.selectors';
import { agendaItemSelectors } from '../../../../../../../app/resource/agenda-item/agenda-item.selectors';
import { IAgendaItem } from '../../../../../../../app/resource/agenda-item/agenda-item.types';
import { IBoardExportOptions } from '../../../../../../dialogs/BoardExportOptionsDialog/types';

export const BoardHeader: FunctionComponent<IBoardHeaderProps> = ({ boardId, meetingId }) => {
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');

  const meetingBoards = useSelect(meetingBoardsPageSelectors.selectMeetingBoards) as IMeetingBoards;
  const currentMeetingBoardIds = meetingBoards[meetingId].boardIds;
  const currentBoard = useSelectWithParams(boardSelectors.selectResourceById, boardId) as IBoard;

  const allMeetingBoards = useSelectWithParams(boardSelectors.selectResourcesById, currentMeetingBoardIds) as IBoard[];

  const tasks = useSelect(taskSimpleSelectors.selectResourceList) as ITaskSimple[];
  const agendaItemIdsByMeeting = useSelect(agendaItemsPageSelectors.selectAgendaItems);
  const meetingAgendaItemIds = agendaItemIdsByMeeting[meetingId];
  const meetingAgendaItems = useSelectWithParams(
    agendaItemSelectors.selectResourcesById,
    meetingAgendaItemIds,
  ) as IAgendaItem[];

  const boardHasAtLeastOneActiveTask = tasks.some((task) => task.boardId == currentBoard.id && task.status === '0');
  const boardHasAtLeastOneTask = tasks.some((task) => task.boardId == currentBoard.id);
  const boardIsConnectedToAtLeastOneAgendaItem = meetingAgendaItems.some((item) => item.boardId == boardId);
  const isTheLastBoard = !allMeetingBoards.some((board) => !board.deleted);

  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId) as IMeetingSimple;
  const dispatch = useDispatch();
  const [showLoader] = useApiRequestState('getBoardLists');

  const onDeleteBoard = useCallback(() => {
    dispatch(deleteBoard(boardId));
    dispatch(closeDialog('confirmation'));
  }, [boardId, dispatch]);

  const openDeleteConfirmationModal = useCallback(
    (operation: 'archive' | 'delete') => {
      dispatch(
        openDialog('confirmation', {
          title: `${operation === 'archive' ? 'Archive' : 'Delete'} board`,
          message: `${
            operation === 'archive' ? 'Archiving' : 'Deleting'
          } a board is permanent and cannot be undone. Do you still want to continue?`,
          onConfirm: onDeleteBoard,
        }),
      );
    },
    [dispatch, onDeleteBoard],
  );

  const openEditBoardFormDialog = useCallback(() => {
    dispatch(openDialog('boardFormDialog', { boardData: currentBoard, meetingId } as IBoardFormProps));
  }, [currentBoard, dispatch, meetingId]);
  const userIds = Array.from(new Set([...meeting.shareWithIds, ...meeting.administratorIds]));

  const isMeetingMember = loggedUser && meeting && isMemberPartOfMeeting(loggedUser, meeting);

  const handleClickOnRemoveButton = (e: any) => {
    if (boardHasAtLeastOneActiveTask || boardIsConnectedToAtLeastOneAgendaItem) {
      // we do nothing
      e.preventDefault();
      return;
    }

    if (boardHasAtLeastOneTask) {
      return openDeleteConfirmationModal('archive');
    } else {
      return openDeleteConfirmationModal('delete');
    }
  };

  const handleBeforeExport = useCallback(
    (proceedWithExport: (queryParams?: ExportQueryParams) => void) => {
      dispatch(
        openDialog('boardExportOptionsDialog', {
          onConfirm: (options: IBoardExportOptions) => {
            proceedWithExport({
              includeCompletedTasks: options.includeCompletedTasks,
            });
          },
        }),
      );
    },
    [dispatch],
  );

  return (
    <div className="panel-header small-header no-border pb-0 board-panel-header">
      {showLoader && (
        <h2 className="bold mr-3">
          <Loader type="TailSpin" color="var(--theme-primary-color)" height={20} width={20} />
        </h2>
      )}
      {!showLoader && <h2 className="bold mr-3">{currentBoard.name}</h2>}

      <div className="flex aic">
        {isMeetingMember && (
          <>
            <a
              className="btn-badge btn-sm mr-05 flex aic jcc darkGray-bg white popup_button"
              data-popup="new_board_popup"
              onClick={openEditBoardFormDialog}
            >
              <i className="icon icon-pencil f-0" />
            </a>
            {!currentBoard.deleted && (
              <>
                <a
                  className={`btn-badge btn-sm flex aic jcc red-bg white ${
                    boardHasAtLeastOneActiveTask || boardIsConnectedToAtLeastOneAgendaItem ? 'disabled-link' : ''
                  }`}
                  onClick={handleClickOnRemoveButton}
                >
                  {boardHasAtLeastOneTask ? <FaArchive size="10" /> : <i className="icon icon-close f-0" />}
                </a>
              </>
            )}
            <InfoTooltips
              showLastBoardInfo={isTheLastBoard}
              showActiveTaskInfo={boardHasAtLeastOneActiveTask}
              showConnectedToAgendaItemsInfo={boardIsConnectedToAtLeastOneAgendaItem}
            />
          </>
        )}
      </div>
      <ExporterButton
        relativePath={`boards/${boardId}/export`}
        className="btn-badge btn-sm ml-2 flex aic jcc green-bg white"
        onBeforeExport={handleBeforeExport}
      />
      <HeaderUsers userIds={userIds} />
    </div>
  );
};

const InfoTooltips: FunctionComponent<IInfoTooltipProps> = ({
  showLastBoardInfo,
  showActiveTaskInfo,
  showConnectedToAgendaItemsInfo,
}) => {
  if (!showLastBoardInfo && !showActiveTaskInfo && !showConnectedToAgendaItemsInfo) {
    return null;
  }
  return (
    <div style={{ lineHeight: '30px' }}>
      <InfoIconPopup>
        <>
          {showLastBoardInfo && <>• Last board cannot be removed.</>}
          {showActiveTaskInfo && <div>• Please finish all active cards before archiving the board.</div>}
          {showConnectedToAgendaItemsInfo && (
            <div>
              • This board or one of its lists is currently linked to an agenda item. Please unlink it before removing
              the board.
            </div>
          )}
        </>
      </InfoIconPopup>
    </div>
  );
};
